import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row, Col, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCopy } from 'react-icons/fa';
import CreatableSelect from 'react-select/creatable';
import WasteCodeSelect from '../../shared/WasteCodeSelect';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import { loadingCodes } from '../../../utils/constants/loadingCodes';
import { FACILITY_TYPES } from '../../../utils/constants/facilityTypes';
import { TECHNICAL_CONDITION_OPTIONS } from '../../../utils/constants/technicalConditions';
import { SHUTDOWN_STATUSES, SHUTDOWN_STATUS_OPTIONS } from '../../../utils/constants/shutdownStatuses';
import { LIMITS_COMPLIANCE_OPTIONS } from '../../../utils/constants/limitsCompliance';
import { fetchClients } from '../../../store/actions/waste';
import CustomModalLarge from '../../../hoc/customModalLarge';
import AddClient from '../../client/add';
import './dayEditor.css';

const toOptions = (codes) => codes.map(({ value, label }) => ({ value, label: `${value} - ${label}` }));

const RECOVERY_CODE_OPTIONS = toOptions(loadingCodes['Zhodnocovanie odpadu']);
const DISPOSAL_CODE_OPTIONS = toOptions(loadingCodes['Zneškodňovanie odpadov']);

// kód nakladania pre zhodnotené/zneškodnené odpady: R1-R13, D1-D15, roztriedené rovnako ako v evidenčnom liste
const PROCESSED_WASTE_LOADING_CODE_GROUPS = [
    { label: 'Zhodnocovanie odpadu', options: RECOVERY_CODE_OPTIONS },
    { label: 'Zneškodňovanie odpadov', options: DISPOSAL_CODE_OPTIONS },
];

// kód nakladania pre prijaté odpady: V, OO | R1-R13 | D1-D15, roztriedené rovnako ako v evidenčnom liste
const RECEIVED_WASTE_LOADING_CODE_GROUPS = [
    { label: 'Iné nakladanie s odpadom', options: toOptions(loadingCodes['Iné nakladanie s odpadom'].filter((c) => c.value === 'V' || c.value === 'OO')) },
    { label: 'Zhodnocovanie odpadu', options: RECOVERY_CODE_OPTIONS },
    { label: 'Zneškodňovanie odpadov', options: DISPOSAL_CODE_OPTIONS },
];

// množstvo v tonách, max. 3 desatinné miesta
const AMOUNT_INPUT_REGEX = /^\d*(\.\d{0,3})?$/;

export const getNested = (obj, path) => path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);

const recordsHint = (count) => {
    if (count === 0) return 'žiadny záznam';
    if (count === 1) return '1 záznam';
    if (count >= 2 && count <= 4) return `${count} záznamy`;
    return `${count} záznamov`;
};

const Chevron = () => (
    <svg className="ol-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const Section = ({ num, title, hint, defaultCollapsed, hasError, children }) => {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    // ak sa v sekcii po pokuse o uloženie objaví chyba, automaticky ju rozbaliť, aby ju bolo vidieť
    useEffect(() => {
        if (hasError) setCollapsed(false);
    }, [hasError]);

    return (
        <div className={`ol-section${collapsed ? ' collapsed' : ''}${hasError ? ' has-error' : ''}`}>
            <div className="ol-section-head" onClick={() => setCollapsed(c => !c)}>
                <div className="ol-section-head-left">
                    <span className="ol-section-num">{num}</span>
                    <span className="ol-section-title">{title}</span>
                    {hint && <span className="ol-section-hint">{hint}</span>}
                    {hasError && <span className="text-danger fw-bold ms-2">obsahuje chyby</span>}
                </div>
                <Chevron />
            </div>
            <div className="ol-section-body">
                {children}
            </div>
        </div>
    );
};

const EmptyState = ({ text, buttonLabel, onAdd }) => (
    <>
        <div className="ol-empty-row">{text}</div>
        <Button variant="outline-primary" size="sm" type="button" onClick={onAdd}>{buttonLabel}</Button>
    </>
);

const OperationalLogEntryFormFields = ({ formik, clients = [], facilityType, responsiblePersons = [] }) => {
    const dispatch = useDispatch();
    const wasteCodes = useSelector(state => state.wasteCodes.data);
    const [isAddClientModalVisible, setIsAddClientModalVisible] = useState(false);
    const [addClientTarget, setAddClientTarget] = useState(null);

    const getWasteCategory = (wasteCodeId) => {
        const waste = wasteCodes.find((w) => w._id === wasteCodeId);
        return waste?.wasteCategory ?? '';
    };

    const addRow = (path, emptyRow) => {
        const current = getNested(formik.values, path) || [];
        formik.setFieldValue(path, [...current, emptyRow]);
    };
    const removeRow = (path, index) => {
        const current = getNested(formik.values, path) || [];
        formik.setFieldValue(path, current.filter((_, i) => i !== index));
    };
    const duplicateRow = (path, index) => {
        const current = getNested(formik.values, path) || [];
        const copy = { ...current[index] };
        formik.setFieldValue(path, [...current.slice(0, index + 1), copy, ...current.slice(index + 1)]);
    };
    const updateRow = (path, index, key, value) => {
        const current = getNested(formik.values, path) || [];
        formik.setFieldValue(path, current.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    };
    const handleAmountChange = (path, index, value) => {
        if (AMOUNT_INPUT_REGEX.test(value)) {
            updateRow(path, index, 'amount', value);
        }
    };
    const handleOpenAddClientModal = (path, index, field = 'producerOrHolder') => {
        setAddClientTarget({ path, index, field });
        setIsAddClientModalVisible(true);
    };

    const handleCloseAddClientModal = () => {
        setIsAddClientModalVisible(false);
        setAddClientTarget(null);
    };

    const handleClientCreated = async(clientId) => {
        try {
            const updatedClients = await dispatch(fetchClients('WASTE')).unwrap();
            const newClient = Array.isArray(updatedClients) ? updatedClients.find((c) => c._id === clientId) : null;
            if (addClientTarget && newClient) {
                updateRow(addClientTarget.path, addClientTarget.index, addClientTarget.field, newClient.companyName);
            }
        } finally {
            handleCloseAddClientModal();
        }
    };

    const isSkladka = facilityType === FACILITY_TYPES.SKLADKA;
    const isOrtut = facilityType === FACILITY_TYPES.ORTUT;

    // chyby sa zobrazujú až po prvom pokuse o uloženie záznamu
    const showErrors = formik.submitCount > 0;
    const errorAt = (path) => (showErrors ? getNested(formik.errors, path) : undefined);
    const sectionHasError = (path) => showErrors && !!getNested(formik.errors, path);

    return (
        <div className="ol-daybook">
            {/* 1) Základné údaje */}
            <Section num={1} title="Základné údaje" hint="zamestnanci, stav zariadenia" defaultCollapsed={false} hasError={sectionHasError('responsiblePersons') || sectionHasError('shutdown.reason')}>
                <Form.Group className="mb-3">
                    <Form.Label>Zodpovední zamestnanci *</Form.Label>
                    <CreatableSelect
                        isMulti
                        isClearable
                        placeholder="Vyberte alebo zadajte meno..."
                        noOptionsMessage={() => 'Žiadne možnosti'}
                        formatCreateLabel={(inputValue) => `Pridať: "${inputValue}"`}
                        options={responsiblePersons.map((p) => ({ value: p, label: p }))}
                        value={formik.values.responsiblePersons.filter(Boolean).map((p) => ({ value: p, label: p }))}
                        onChange={(selected) => formik.setFieldValue('responsiblePersons', (selected ?? []).map((o) => o.value))}
                        styles={{ control: (base) => (errorAt('responsiblePersons') ? { ...base, borderColor: '#dc3545' } : base) }}
                    />
                    {errorAt('responsiblePersons') && <div className="text-danger small mt-1">{errorAt('responsiblePersons')}</div>}
                </Form.Group>

                {!isOrtut && (
                    <>
                        <Row className="mt-3">
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Technický stav zariadenia</Form.Label>
                                    <Form.Select {...formik.getFieldProps('technicalCondition')}>
                                        <option value="">Vyberte stav...</option>
                                        {TECHNICAL_CONDITION_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Časové využitie zariadenia</Form.Label>
                                    <Form.Control type="text" placeholder="napr. 06:00 - 18:00" {...formik.getFieldProps('operatingHours')} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label>Odstavenie zariadenia</Form.Label>
                                    <Form.Select
                                        value={formik.values.shutdown.status}
                                        onChange={(e) => formik.setFieldValue('shutdown.status', e.target.value)}
                                    >
                                        {SHUTDOWN_STATUS_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label>Dodržiavanie limitov a technických podmienok</Form.Label>
                                    <Form.Select {...formik.getFieldProps('limitsCompliance')}>
                                        {LIMITS_COMPLIANCE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        {formik.values.shutdown.status !== SHUTDOWN_STATUSES.NONE && (
                            <Form.Group className="mb-2">
                                <Form.Label>Čas a dôvod odstavenia</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="napr. 13:00-15:30, plánovaná údržba"
                                    value={formik.values.shutdown.reason}
                                    onChange={(e) => formik.setFieldValue('shutdown.reason', e.target.value)}
                                    isInvalid={!!errorAt('shutdown.reason')}
                                />
                                <Form.Control.Feedback type="invalid">{errorAt('shutdown.reason')}</Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </>
                )}
            </Section>

            {!isOrtut && (
            <>
            {/* 2) Prijaté odpady */}
            <Section num={2} title="Prijaté odpady" hint={recordsHint(formik.values.wasteReceived.length)} defaultCollapsed={false} hasError={sectionHasError('wasteReceived')}>
                {formik.values.wasteReceived.length === 0 ? (
                    <EmptyState
                        text="Dnes nebol prijatý žiadny odpad."
                        buttonLabel="+ Pridať prijatý odpad"
                        onAdd={() => addRow('wasteReceived', { wasteCode: null, amount: '', unit: 't', loadingCode: '', producerOrHolder: '' })}
                    />
                ) : (
                    <>
                        <div className="ol-table-scroll">
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Kód odpadu</th><th style={{ width: '80px' }} className="text-center">Kategória</th><th style={{ width: '110px' }}>Množstvo (t)</th><th style={{ width: '160px' }}>Kód nakladania</th><th style={{ width: '240px' }}>Pôvodca/držiteľ</th><th></th></tr>
                                {formik.values.wasteReceived.map((line, index) => (
                                    <tr key={index}>
                                        <td style={{ minWidth: '345px' }}>
                                            <WasteCodeSelect
                                                className=""
                                                value={line.wasteCode}
                                                onSelect={(waste) => updateRow('wasteReceived', index, 'wasteCode', waste ? waste._id : null)}
                                                isInvalid={!!errorAt(`wasteReceived.${index}.wasteCode`)}
                                                errorMessage={errorAt(`wasteReceived.${index}.wasteCode`)}
                                            />
                                        </td>
                                        <td style={{ width: '80px' }} className="text-center">{getWasteCategory(line.wasteCode)}</td>
                                        <td style={{ width: '110px' }}>
                                            <Form.Control type="number" step="0.001" min="0" value={line.amount ?? ''} onChange={(e) => handleAmountChange('wasteReceived', index, e.target.value)} isInvalid={!!errorAt(`wasteReceived.${index}.amount`)} />
                                            <Form.Control.Feedback type="invalid">{errorAt(`wasteReceived.${index}.amount`)}</Form.Control.Feedback>
                                        </td>
                                        <td style={{ width: '160px' }}>
                                            <Form.Select
                                                value={line.loadingCode ?? ''}
                                                onChange={(e) => updateRow('wasteReceived', index, 'loadingCode', e.target.value)}
                                                isInvalid={!!errorAt(`wasteReceived.${index}.loadingCode`)}
                                            >
                                                <option value="">Vyberte kód...</option>
                                                {RECEIVED_WASTE_LOADING_CODE_GROUPS.map((group) => (
                                                    <optgroup key={group.label} label={group.label}>
                                                        {group.options.map((option) => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errorAt(`wasteReceived.${index}.loadingCode`)}</Form.Control.Feedback>
                                        </td>
                                        <td style={{ width: '240px' }}>
                                            <div className="d-flex gap-1">
                                                <Form.Select
                                                    value={line.producerOrHolder ?? ''}
                                                    onChange={(e) => updateRow('wasteReceived', index, 'producerOrHolder', e.target.value)}
                                                >
                                                    <option value="">Vyberte odberateľa...</option>
                                                    {clients.map((client) => (
                                                        <option key={client._id} value={client.companyName}>{client.companyName}</option>
                                                    ))}
                                                </Form.Select>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Pridať pôvodcu/držiteľa</Tooltip>}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        type="button"
                                                        className="flex-shrink-0"
                                                        onClick={() => handleOpenAddClientModal('wasteReceived', index)}
                                                    >
                                                        +
                                                    </Button>
                                                </OverlayTrigger>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať riadok</Tooltip>}>
                                                    <button type="button" className="ol-row-copy" onClick={() => duplicateRow('wasteReceived', index)}><FaCopy size={13} /></button>
                                                </OverlayTrigger>
                                                <button type="button" className="ol-row-del" onClick={() => removeRow('wasteReceived', index)}>✕</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('wasteReceived', { wasteCode: null, amount: '', unit: 't', loadingCode: '', producerOrHolder: '' })}>+ Pridať prijatý odpad</Button>
                    </>
                )}
            </Section>

            {/* 3) Zhodnotené / zneškodnené odpady */}
            <Section num={3} title="Zhodnotené / zneškodnené odpady" hint={recordsHint(formik.values.wasteProcessed.length)} defaultCollapsed={false} hasError={sectionHasError('wasteProcessed')}>
                {formik.values.wasteProcessed.length === 0 ? (
                    <EmptyState
                        text="Dnes nebol spracovaný žiadny odpad."
                        buttonLabel="+ Pridať spracovaný odpad"
                        onAdd={() => addRow('wasteProcessed', { wasteCode: null, amount: '', unit: 't', loadingCode: '' })}
                    />
                ) : (
                    <>
                        <div className="ol-table-scroll">
                        <table className="ol-rowtable" style={{ tableLayout: 'fixed' }}>
                            <tbody>
                                <tr><th style={{ width: '38%' }}>Kód odpadu</th><th style={{ width: '110px' }}>Množstvo (t)</th><th style={{ width: '38%' }}>Kód nakladania</th><th style={{ width: '70px' }}></th></tr>
                                {formik.values.wasteProcessed.map((line, index) => (
                                    <tr key={index}>
                                        <td>
                                            <WasteCodeSelect
                                                className=""
                                                value={line.wasteCode}
                                                onSelect={(waste) => updateRow('wasteProcessed', index, 'wasteCode', waste ? waste._id : null)}
                                                isInvalid={!!errorAt(`wasteProcessed.${index}.wasteCode`)}
                                                errorMessage={errorAt(`wasteProcessed.${index}.wasteCode`)}
                                            />
                                        </td>
                                        <td style={{ width: '110px' }}>
                                            <Form.Control type="number" step="0.001" min="0" value={line.amount ?? ''} onChange={(e) => handleAmountChange('wasteProcessed', index, e.target.value)} isInvalid={!!errorAt(`wasteProcessed.${index}.amount`)} />
                                            <Form.Control.Feedback type="invalid">{errorAt(`wasteProcessed.${index}.amount`)}</Form.Control.Feedback>
                                        </td>
                                        <td>
                                            <Form.Select
                                                value={line.loadingCode ?? ''}
                                                onChange={(e) => updateRow('wasteProcessed', index, 'loadingCode', e.target.value)}
                                                isInvalid={!!errorAt(`wasteProcessed.${index}.loadingCode`)}
                                            >
                                                <option value="">Vyberte kód...</option>
                                                {PROCESSED_WASTE_LOADING_CODE_GROUPS.map((group) => (
                                                    <optgroup key={group.label} label={group.label}>
                                                        {group.options.map((option) => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">{errorAt(`wasteProcessed.${index}.loadingCode`)}</Form.Control.Feedback>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať riadok</Tooltip>}>
                                                    <button type="button" className="ol-row-copy" onClick={() => duplicateRow('wasteProcessed', index)}><FaCopy size={13} /></button>
                                                </OverlayTrigger>
                                                <button type="button" className="ol-row-del" onClick={() => removeRow('wasteProcessed', index)}>✕</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('wasteProcessed', { wasteCode: null, amount: '', unit: 't', loadingCode: '' })}>+ Pridať spracovaný odpad</Button>
                    </>
                )}
            </Section>

            {/* 4) Odpady vznikajúce v zariadení */}
            <Section num={4} title="Odpady vznikajúce priamo v zariadení" hint="nakladanie s tuhými a kvapalnými odpadmi z prevádzky" defaultCollapsed={!formik.values.solidLiquidWasteHandling}>
                <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Napíšte, čo v zariadení dnes vzniklo a ako sa s tým naložilo..."
                    {...formik.getFieldProps('solidLiquidWasteHandling')}
                />
            </Section>

            {/* 5) Odobraté vzorky */}
            <Section num={5} title="Odobraté vzorky odpadov" hint={recordsHint(formik.values.samples.length)} defaultCollapsed={formik.values.samples.length === 0} hasError={sectionHasError('samples')}>
                {formik.values.samples.length === 0 ? (
                    <EmptyState
                        text="Dnes nebola odobratá žiadna vzorka."
                        buttonLabel="+ Zaznamenať odber vzorky"
                        onAdd={() => addRow('samples', { takenAt: new Date(), description: '', resultsSummary: '' })}
                    />
                ) : (
                    <>
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Dátum odberu</th><th></th></tr>
                                {formik.values.samples.map((sample, index) => (
                                    <>
                                        <tr key={index}>
                                            <td style={{ minWidth: '140px' }}>
                                                <DatePicker
                                                    selected={sample.takenAt ? new Date(sample.takenAt) : null}
                                                    onChange={(date) => updateRow('samples', index, 'takenAt', date)}
                                                    dateFormat="dd.MM.yyyy"
                                                    className={`form-control${errorAt(`samples.${index}.takenAt`) ? ' is-invalid' : ''}`}
                                                    locale={sk}
                                                />
                                                {errorAt(`samples.${index}.takenAt`) && <div className="invalid-feedback d-block">{errorAt(`samples.${index}.takenAt`)}</div>}
                                            </td>
                                            <td><button type="button" className="ol-row-del" onClick={() => removeRow('samples', index)}>✕</button></td>
                                        </tr>
                                        <tr key={`${index}-desc`} className="ol-reason-row">
                                            <td colSpan={2}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Popis..."
                                                    value={sample.description ?? ''}
                                                    onChange={(e) => updateRow('samples', index, 'description', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                        <tr key={`${index}-results`} className="ol-reason-row">
                                            <td colSpan={2}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Výsledky analýzy..."
                                                    value={sample.resultsSummary ?? ''}
                                                    onChange={(e) => updateRow('samples', index, 'resultsSummary', e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('samples', { takenAt: new Date(), description: '', resultsSummary: '' })}>+ Zaznamenať odber vzorky</Button>
                    </>
                )}
            </Section>

            {/* 6) Poruchy a havárie */}
            <Section num={6} title="Poruchy a havárie" hint={recordsHint(formik.values.malfunctions.length)} defaultCollapsed={formik.values.malfunctions.length === 0} hasError={sectionHasError('malfunctions')}>
                {formik.values.malfunctions.length === 0 ? (
                    <EmptyState
                        text="Dnes zatiaľ nezaznamenaná žiadna porucha."
                        buttonLabel="+ Zaznamenať poruchu"
                        onAdd={() => addRow('malfunctions', { description: '', resolution: '' })}
                    />
                ) : (
                    <>
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Popis poruchy/havárie</th><th>Spôsob odstránenia</th><th></th></tr>
                                {formik.values.malfunctions.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control type="text" value={item.description ?? ''} onChange={(e) => updateRow('malfunctions', index, 'description', e.target.value)} isInvalid={!!errorAt(`malfunctions.${index}.description`)} />
                                            <Form.Control.Feedback type="invalid">{errorAt(`malfunctions.${index}.description`)}</Form.Control.Feedback>
                                        </td>
                                        <td><Form.Control type="text" value={item.resolution ?? ''} onChange={(e) => updateRow('malfunctions', index, 'resolution', e.target.value)} /></td>
                                        <td><button type="button" className="ol-row-del" onClick={() => removeRow('malfunctions', index)}>✕</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('malfunctions', { description: '', resolution: '' })}>+ Zaznamenať poruchu</Button>
                    </>
                )}
            </Section>

            {/* 7) Neprevzatý odpad */}
            <Section num={7} title="Neprevzatý odpad" hint={recordsHint(formik.values.wasteRejected.length)} defaultCollapsed={formik.values.wasteRejected.length === 0} hasError={sectionHasError('wasteRejected')}>
                {formik.values.wasteRejected.length === 0 ? (
                    <EmptyState
                        text="Všetok ponúkaný odpad bol dnes prevzatý."
                        buttonLabel="+ Zaznamenať neprevzatie"
                        onAdd={() => addRow('wasteRejected', { wasteCode: null, amount: '', producerOrHolder: '', reason: '' })}
                    />
                ) : (
                    <>
                        <div className="ol-table-scroll">
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Kód odpadu</th><th style={{ width: '110px' }}>Množstvo (t)</th><th style={{ width: '240px' }}>Pôvodca/držiteľ</th><th></th></tr>
                                {formik.values.wasteRejected.map((line, index) => (
                                    <>
                                        <tr key={index}>
                                            <td style={{ minWidth: '200px' }}>
                                                <WasteCodeSelect
                                                    className=""
                                                    value={line.wasteCode}
                                                    onSelect={(waste) => updateRow('wasteRejected', index, 'wasteCode', waste ? waste._id : null)}
                                                />
                                            </td>
                                            <td style={{ width: '110px' }}><Form.Control type="number" step="0.001" min="0" value={line.amount ?? ''} onChange={(e) => handleAmountChange('wasteRejected', index, e.target.value)} /></td>
                                            <td style={{ width: '240px' }}>
                                                <div className="d-flex gap-1">
                                                    <Form.Select
                                                        value={line.producerOrHolder ?? ''}
                                                        onChange={(e) => updateRow('wasteRejected', index, 'producerOrHolder', e.target.value)}
                                                    >
                                                        <option value="">Vyberte pôvodcu/držiteľa...</option>
                                                        {clients.map((client) => (
                                                            <option key={client._id} value={client.companyName}>{client.companyName}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Pridať pôvodcu/držiteľa</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            type="button"
                                                            className="flex-shrink-0"
                                                            onClick={() => handleOpenAddClientModal('wasteRejected', index)}
                                                        >
                                                            +
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať riadok</Tooltip>}>
                                                        <button type="button" className="ol-row-copy" onClick={() => duplicateRow('wasteRejected', index)}><FaCopy size={13} /></button>
                                                    </OverlayTrigger>
                                                    <button type="button" className="ol-row-del" onClick={() => removeRow('wasteRejected', index)}>✕</button>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr key={`${index}-reason`} className="ol-reason-row">
                                            <td colSpan={4}>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Zdôvodnenie neprevzatia..."
                                                    value={line.reason ?? ''}
                                                    onChange={(e) => updateRow('wasteRejected', index, 'reason', e.target.value)}
                                                    isInvalid={!!errorAt(`wasteRejected.${index}.reason`)}
                                                />
                                                <Form.Control.Feedback type="invalid">{errorAt(`wasteRejected.${index}.reason`)}</Form.Control.Feedback>
                                            </td>
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('wasteRejected', { wasteCode: null, amount: '', producerOrHolder: '', reason: '' })}>+ Zaznamenať neprevzatie</Button>
                    </>
                )}
            </Section>

            {/* 8) Údržba a opravy */}
            <Section num={8} title="Údržba a opravy" hint={recordsHint(formik.values.maintenance.length)} defaultCollapsed={formik.values.maintenance.length === 0} hasError={sectionHasError('maintenance')}>
                {formik.values.maintenance.length === 0 ? (
                    <EmptyState
                        text="Dnes sa nevykonávala žiadna údržba ani oprava."
                        buttonLabel="+ Zaznamenať údržbu/opravu"
                        onAdd={() => addRow('maintenance', { description: '' })}
                    />
                ) : (
                    <>
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Popis údržby/opravy</th><th></th></tr>
                                {formik.values.maintenance.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control type="text" value={item.description ?? ''} onChange={(e) => updateRow('maintenance', index, 'description', e.target.value)} isInvalid={!!errorAt(`maintenance.${index}.description`)} />
                                            <Form.Control.Feedback type="invalid">{errorAt(`maintenance.${index}.description`)}</Form.Control.Feedback>
                                        </td>
                                        <td><button type="button" className="ol-row-del" onClick={() => removeRow('maintenance', index)}>✕</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('maintenance', { description: '' })}>+ Zaznamenať údržbu/opravu</Button>
                    </>
                )}
            </Section>

            {/* 9) Vykonané kontroly */}
            <Section num={9} title="Vykonané kontroly" hint={recordsHint(formik.values.authorityInspections.length)} defaultCollapsed={formik.values.authorityInspections.length === 0} hasError={sectionHasError('authorityInspections')}>
                {formik.values.authorityInspections.length === 0 ? (
                    <EmptyState
                        text="Dnes neprebehla žiadna kontrola."
                        buttonLabel="+ Zaznamenať kontrolu"
                        onAdd={() => addRow('authorityInspections', { authority: '', from: null, to: null, notes: '' })}
                    />
                ) : (
                    <>
                        <table className="ol-rowtable">
                            <tbody>
                                <tr><th>Kontrolný orgán</th><th>Od</th><th>Do</th><th>Poznámka</th><th></th></tr>
                                {formik.values.authorityInspections.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Control type="text" value={item.authority ?? ''} onChange={(e) => updateRow('authorityInspections', index, 'authority', e.target.value)} isInvalid={!!errorAt(`authorityInspections.${index}.authority`)} />
                                            <Form.Control.Feedback type="invalid">{errorAt(`authorityInspections.${index}.authority`)}</Form.Control.Feedback>
                                        </td>
                                        <td style={{ minWidth: '120px' }}>
                                            <DatePicker
                                                selected={item.from ? new Date(item.from) : null}
                                                onChange={(date) => updateRow('authorityInspections', index, 'from', date)}
                                                dateFormat="dd.MM.yyyy"
                                                className="form-control"
                                                locale={sk}
                                            />
                                        </td>
                                        <td style={{ minWidth: '120px' }}>
                                            <DatePicker
                                                selected={item.to ? new Date(item.to) : null}
                                                onChange={(date) => updateRow('authorityInspections', index, 'to', date)}
                                                dateFormat="dd.MM.yyyy"
                                                className="form-control"
                                                locale={sk}
                                            />
                                        </td>
                                        <td><Form.Control type="text" value={item.notes ?? ''} onChange={(e) => updateRow('authorityInspections', index, 'notes', e.target.value)} /></td>
                                        <td><button type="button" className="ol-row-del" onClick={() => removeRow('authorityInspections', index)}>✕</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('authorityInspections', { authority: '', from: null, to: null, notes: '' })}>+ Zaznamenať kontrolu</Button>
                    </>
                )}
            </Section>

            {/* 10) Ďalšie prevádzkové činnosti */}
            <Section num={10} title="Ďalšie prevádzkové činnosti" hint="voľná poznámka" defaultCollapsed={!formik.values.otherActivities}>
                <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Napíšte akékoľvek ďalšie relevantné udalosti dňa..."
                    {...formik.getFieldProps('otherActivities')}
                />
            </Section>
            </>
            )}

            {/* 11) Skládka odpadov — doplňujúce údaje */}
            {isSkladka && (
                <Section num={11} title="Skládka odpadov — doplňujúce údaje" hint="povinné pre skládku odpadov" defaultCollapsed={false} hasError={sectionHasError('landfillDetails')}>
                    <Form.Group className="mb-2">
                        <Form.Label>Umiestnenie nebezpečného odpadu podľa topografickej siete *</Form.Label>
                        <Form.Control
                            type="text"
                            value={formik.values.landfillDetails.hazardousWastePlacement}
                            onChange={(e) => formik.setFieldValue('landfillDetails.hazardousWastePlacement', e.target.value)}
                            isInvalid={!!errorAt('landfillDetails.hazardousWastePlacement')}
                        />
                        <Form.Control.Feedback type="invalid">{errorAt('landfillDetails.hazardousWastePlacement')}</Form.Control.Feedback>
                    </Form.Group>

                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Zhutnenie odpadu *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formik.values.landfillDetails.compaction}
                                    onChange={(e) => formik.setFieldValue('landfillDetails.compaction', e.target.value)}
                                    isInvalid={!!errorAt('landfillDetails.compaction')}
                                />
                                <Form.Control.Feedback type="invalid">{errorAt('landfillDetails.compaction')}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Hrúbka pracovnej vrstvy a prekrytie odpadu *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formik.values.landfillDetails.layerThicknessAndCovering}
                                    onChange={(e) => formik.setFieldValue('landfillDetails.layerThicknessAndCovering', e.target.value)}
                                    isInvalid={!!errorAt('landfillDetails.layerThicknessAndCovering')}
                                />
                                <Form.Control.Feedback type="invalid">{errorAt('landfillDetails.layerThicknessAndCovering')}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Label className="mt-2">Odber vzoriek z monitorovacích vrtov</Form.Label>
                    {formik.values.landfillDetails.monitoringWellSamples.length === 0 ? (
                        <EmptyState
                            text="Dnes nebola odobratá vzorka z monitorovacieho vrtu."
                            buttonLabel="+ Pridať vzorku z vrtu"
                            onAdd={() => addRow('landfillDetails.monitoringWellSamples', { wellId: '', takenAt: new Date(), resultsSummary: '' })}
                        />
                    ) : (
                        <>
                            <table className="ol-rowtable">
                                <tbody>
                                    <tr><th>Označenie vrtu</th><th>Dátum odberu</th><th>Výsledky</th><th></th></tr>
                                    {formik.values.landfillDetails.monitoringWellSamples.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Form.Control type="text" value={item.wellId ?? ''} onChange={(e) => updateRow('landfillDetails.monitoringWellSamples', index, 'wellId', e.target.value)} isInvalid={!!errorAt(`landfillDetails.monitoringWellSamples.${index}.wellId`)} />
                                                <Form.Control.Feedback type="invalid">{errorAt(`landfillDetails.monitoringWellSamples.${index}.wellId`)}</Form.Control.Feedback>
                                            </td>
                                            <td style={{ minWidth: '140px' }}>
                                                <DatePicker
                                                    selected={item.takenAt ? new Date(item.takenAt) : null}
                                                    onChange={(date) => updateRow('landfillDetails.monitoringWellSamples', index, 'takenAt', date)}
                                                    dateFormat="dd.MM.yyyy"
                                                    className={`form-control${errorAt(`landfillDetails.monitoringWellSamples.${index}.takenAt`) ? ' is-invalid' : ''}`}
                                                    locale={sk}
                                                />
                                                {errorAt(`landfillDetails.monitoringWellSamples.${index}.takenAt`) && <div className="invalid-feedback d-block">{errorAt(`landfillDetails.monitoringWellSamples.${index}.takenAt`)}</div>}
                                            </td>
                                            <td><Form.Control type="text" value={item.resultsSummary ?? ''} onChange={(e) => updateRow('landfillDetails.monitoringWellSamples', index, 'resultsSummary', e.target.value)} /></td>
                                            <td><button type="button" className="ol-row-del" onClick={() => removeRow('landfillDetails.monitoringWellSamples', index)}>✕</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('landfillDetails.monitoringWellSamples', { wellId: '', takenAt: new Date(), resultsSummary: '' })}>+ Pridať vzorku z vrtu</Button>
                        </>
                    )}

                    <Form.Label className="mt-3">Inertné odpady/materiály použité na prekrytie</Form.Label>
                    {formik.values.landfillDetails.inertMaterialsUsed.length === 0 ? (
                        <EmptyState
                            text="Dnes nebol na prekrytie použitý žiadny inertný materiál."
                            buttonLabel="+ Pridať materiál"
                            onAdd={() => addRow('landfillDetails.inertMaterialsUsed', { materialName: '', amount: '', unit: 't' })}
                        />
                    ) : (
                        <>
                            <table className="ol-rowtable">
                                <tbody>
                                    <tr><th>Názov materiálu</th><th>Množstvo</th><th>Jednotka</th><th></th></tr>
                                    {formik.values.landfillDetails.inertMaterialsUsed.map((item, index) => (
                                        <tr key={index}>
                                            <td><Form.Control type="text" value={item.materialName ?? ''} onChange={(e) => updateRow('landfillDetails.inertMaterialsUsed', index, 'materialName', e.target.value)} /></td>
                                            <td><Form.Control type="number" step="0.001" min="0" value={item.amount ?? ''} onChange={(e) => handleAmountChange('landfillDetails.inertMaterialsUsed', index, e.target.value)} /></td>
                                            <td style={{ width: '90px' }}><Form.Control type="text" value={item.unit ?? ''} onChange={(e) => updateRow('landfillDetails.inertMaterialsUsed', index, 'unit', e.target.value)} /></td>
                                            <td><button type="button" className="ol-row-del" onClick={() => removeRow('landfillDetails.inertMaterialsUsed', index)}>✕</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('landfillDetails.inertMaterialsUsed', { materialName: '', amount: '', unit: 't' })}>+ Pridať materiál</Button>
                        </>
                    )}

                    <Form.Group className="mt-3 mb-2">
                        <Form.Label>Prečerpávanie vôd akumulovaných v telese skládky</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={formik.values.landfillDetails.waterPumping}
                            onChange={(e) => formik.setFieldValue('landfillDetails.waterPumping', e.target.value)}
                        />
                    </Form.Group>
                </Section>
            )}

            {/* 11) Odpadová ortuť — doplňujúce údaje */}
            {isOrtut && (
                <Section num={11} title="Odpadová ortuť — doplňujúce údaje" hint="povinné pre úložisko odpadovej ortuti" defaultCollapsed={false} hasError={sectionHasError('mercuryReceived') || sectionHasError('mercuryDispatched')}>
                    <Form.Label>Prijatá odpadová ortuť</Form.Label>
                    {formik.values.mercuryReceived.length === 0 ? (
                        <EmptyState
                            text="Dnes nebola prijatá žiadna odpadová ortuť."
                            buttonLabel="+ Pridať prijatú ortuť"
                            onAdd={() => addRow('mercuryReceived', { amount: '', producerOrHolder: '', containerCertificateNumber: '' })}
                        />
                    ) : (
                        <>
                            <div className="ol-table-scroll">
                            <table className="ol-rowtable">
                                <tbody>
                                    <tr><th style={{ width: '110px' }}>Množstvo (kg)</th><th style={{ width: '240px' }}>Pôvodca/držiteľ</th><th>Číslo potvrdenia o kontajneri</th><th></th></tr>
                                    {formik.values.mercuryReceived.map((line, index) => (
                                        <tr key={index}>
                                            <td style={{ width: '110px' }}>
                                                <Form.Control type="number" step="0.001" min="0" value={line.amount ?? ''} onChange={(e) => handleAmountChange('mercuryReceived', index, e.target.value)} isInvalid={!!errorAt(`mercuryReceived.${index}.amount`)} />
                                                <Form.Control.Feedback type="invalid">{errorAt(`mercuryReceived.${index}.amount`)}</Form.Control.Feedback>
                                            </td>
                                            <td style={{ width: '240px' }}>
                                                <div className="d-flex gap-1">
                                                    <Form.Select
                                                        value={line.producerOrHolder ?? ''}
                                                        onChange={(e) => updateRow('mercuryReceived', index, 'producerOrHolder', e.target.value)}
                                                        isInvalid={!!errorAt(`mercuryReceived.${index}.producerOrHolder`)}
                                                    >
                                                        <option value="">Vyberte pôvodcu/držiteľa...</option>
                                                        {clients.map((client) => (
                                                            <option key={client._id} value={client.companyName}>{client.companyName}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Pridať pôvodcu/držiteľa</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            type="button"
                                                            className="flex-shrink-0"
                                                            onClick={() => handleOpenAddClientModal('mercuryReceived', index)}
                                                        >
                                                            +
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                                {errorAt(`mercuryReceived.${index}.producerOrHolder`) && <div className="invalid-feedback d-block">{errorAt(`mercuryReceived.${index}.producerOrHolder`)}</div>}
                                            </td>
                                            <td>
                                                <Form.Control type="text" value={line.containerCertificateNumber ?? ''} onChange={(e) => updateRow('mercuryReceived', index, 'containerCertificateNumber', e.target.value)} isInvalid={!!errorAt(`mercuryReceived.${index}.containerCertificateNumber`)} />
                                                <Form.Control.Feedback type="invalid">{errorAt(`mercuryReceived.${index}.containerCertificateNumber`)}</Form.Control.Feedback>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať riadok</Tooltip>}>
                                                        <button type="button" className="ol-row-copy" onClick={() => duplicateRow('mercuryReceived', index)}><FaCopy size={13} /></button>
                                                    </OverlayTrigger>
                                                    <button type="button" className="ol-row-del" onClick={() => removeRow('mercuryReceived', index)}>✕</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                            <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('mercuryReceived', { amount: '', producerOrHolder: '', containerCertificateNumber: '' })}>+ Pridať prijatú ortuť</Button>
                        </>
                    )}

                    <Form.Label className="mt-3">Vyskladnená odpadová ortuť</Form.Label>
                    {formik.values.mercuryDispatched.length === 0 ? (
                        <EmptyState
                            text="Dnes nebola vyskladnená žiadna odpadová ortuť."
                            buttonLabel="+ Pridať vyskladnenú ortuť"
                            onAdd={() => addRow('mercuryDispatched', { amount: '', recipient: '', treatmentFacility: '', permanentStorageFacility: '' })}
                        />
                    ) : (
                        <>
                            <div className="ol-table-scroll">
                            <table className="ol-rowtable">
                                <tbody>
                                    <tr><th style={{ width: '110px' }}>Množstvo (kg)</th><th style={{ width: '220px' }}>Príjemca</th><th>Zariadenie na úpravu</th><th>Úložisko trvalého uskladnenia</th><th></th></tr>
                                    {formik.values.mercuryDispatched.map((line, index) => (
                                        <tr key={index}>
                                            <td style={{ width: '110px' }}>
                                                <Form.Control type="number" step="0.001" min="0" value={line.amount ?? ''} onChange={(e) => handleAmountChange('mercuryDispatched', index, e.target.value)} isInvalid={!!errorAt(`mercuryDispatched.${index}.amount`)} />
                                                <Form.Control.Feedback type="invalid">{errorAt(`mercuryDispatched.${index}.amount`)}</Form.Control.Feedback>
                                            </td>
                                            <td style={{ width: '220px' }}>
                                                <div className="d-flex gap-1">
                                                    <Form.Select
                                                        value={line.recipient ?? ''}
                                                        onChange={(e) => updateRow('mercuryDispatched', index, 'recipient', e.target.value)}
                                                        isInvalid={!!errorAt(`mercuryDispatched.${index}.recipient`)}
                                                    >
                                                        <option value="">Vyberte príjemcu...</option>
                                                        {clients.map((client) => (
                                                            <option key={client._id} value={client.companyName}>{client.companyName}</option>
                                                        ))}
                                                    </Form.Select>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Pridať príjemcu</Tooltip>}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            type="button"
                                                            className="flex-shrink-0"
                                                            onClick={() => handleOpenAddClientModal('mercuryDispatched', index, 'recipient')}
                                                        >
                                                            +
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                                {errorAt(`mercuryDispatched.${index}.recipient`) && <div className="invalid-feedback d-block">{errorAt(`mercuryDispatched.${index}.recipient`)}</div>}
                                            </td>
                                            <td><Form.Control type="text" value={line.treatmentFacility ?? ''} onChange={(e) => updateRow('mercuryDispatched', index, 'treatmentFacility', e.target.value)} /></td>
                                            <td><Form.Control type="text" value={line.permanentStorageFacility ?? ''} onChange={(e) => updateRow('mercuryDispatched', index, 'permanentStorageFacility', e.target.value)} /></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Kopírovať riadok</Tooltip>}>
                                                        <button type="button" className="ol-row-copy" onClick={() => duplicateRow('mercuryDispatched', index)}><FaCopy size={13} /></button>
                                                    </OverlayTrigger>
                                                    <button type="button" className="ol-row-del" onClick={() => removeRow('mercuryDispatched', index)}>✕</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                            <Button variant="outline-primary" size="sm" type="button" onClick={() => addRow('mercuryDispatched', { amount: '', recipient: '', treatmentFacility: '', permanentStorageFacility: '' })}>+ Pridať vyskladnenú ortuť</Button>
                        </>
                    )}
                </Section>
            )}

            <CustomModalLarge isVisible={isAddClientModalVisible} onClose={handleCloseAddClientModal}>
                <AddClient
                    isVisible={isAddClientModalVisible}
                    onClose={handleCloseAddClientModal}
                    onSuccess={handleClientCreated}
                    initialCategories={['WASTE']}
                />
            </CustomModalLarge>
        </div>
    );
};

export default OperationalLogEntryFormFields;
